import React, { useState, useCallback } from 'react';
import { Button } from 'antd';
import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import { t } from 'i18n';

/** 编辑态 hook 的入参 */
export interface UseEditToolbarOptions {
    /** 进入编辑时调用，用于把 waypoint 等数据同步到草稿 */
    onStartEdit?: () => void;
}

/** 编辑态 hook 的返回值 */
export interface UseEditToolbarResult {
    isEditing: boolean;
    startEdit: () => void;
    handleCancel: () => void;
    /** 保存成功后调用，用于关闭编辑态 */
    exitEdit: () => void;
}

/**
 * 步骤详情公共编辑态：isEditing、startEdit、handleCancel、exitEdit
 */
export function useEditToolbar(options: UseEditToolbarOptions = {}): UseEditToolbarResult {
    const { onStartEdit } = options;
    const [isEditing, setIsEditing] = useState(false);

    const startEdit = useCallback(() => {
        onStartEdit?.();
        setIsEditing(true);
    }, [onStartEdit]);

    const handleCancel = useCallback(() => {
        setIsEditing(false);
    }, []);

    const exitEdit = useCallback(() => {
        setIsEditing(false);
    }, []);

    return { isEditing, startEdit, handleCancel, exitEdit };
}

export interface EditToolbarProps {
    isEditing: boolean;
    /** 是否可进入步骤编辑（权限 + 任务策略，由父组件汇总） */
    allowEdit: boolean;
    onStartEdit: () => void;
    onSave: () => void;
    onCancel: () => void;
}

/**
 * 步骤详情公共编辑工具栏：编辑 / 保存 / 取消 按钮
 */
const EditToolbar: React.FC<EditToolbarProps> = ({
    isEditing,
    allowEdit,
    onStartEdit,
    onSave,
    onCancel,
}) => {
    const showEditButton = allowEdit;

    const handleStartEdit = () => {
        if (!allowEdit) return;
        onStartEdit();
    };

    return (
        <div className="absolute top-0 right-0 flex gap-2">
            {isEditing ? (
                <>
                    <Button size="small" type="text" icon={<CheckOutlined />} onClick={onSave}>
                        {t('tms.task.drawer.save')}
                    </Button>
                    <Button size="small" type="text" icon={<CloseOutlined />} onClick={onCancel}>
                        {t('tms.task.drawer.cancel')}
                    </Button>
                </>
            ) : (
                showEditButton && (
                    <Button size="small" type="text" icon={<EditOutlined />} onClick={handleStartEdit}>
                        {t('tms.task.drawer.edit')}
                    </Button>
                )
            )}
        </div>
    );
};

export default EditToolbar;
